import { memo, useMemo, useRef, useCallback, StrictMode } from 'react';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import { APPROVAL_STATES, CUSTOM_RATES, ROW_HEIGHT, ROW_STICKY_COUNT, TOP_HEADERS_HEIGHT } from './CONSTANTS';
import { ScrollToContextProvider } from './context/ScrollToContext';
import { ActivityDto, ApprovalState, ProjectApprovalData, ProjectDto, TimeUserDto } from '../../../../api/generated/data-contracts';
import { SelectionBar } from '../SelectionBar/SelectionBar';
import { RowWrapper } from './components/Rows/RowWrapper/RowWrapper';
import { Headers } from './components/Headers';
import { useApprovalStore, useGridRenderableRows, useInitializeApprovalStore } from '../../../../_grid/grid3';
import { Dropdown, IDropdownOption, IRenderFunction, Stack } from '@fluentui/react';
import shallow from 'zustand/shallow';
import { FilterDropdown } from '../FilterDropdown/FilterDropdown';
import { useTimeUIConfig } from '../../../../hooks/queries/useTimeUIConfig';

export const ApprovalGrid = memo(function ApprovalGrid({ data }: { data: ProjectApprovalData }) {
    const allRows = useInitializeApprovalStore(data);

    const renderableRows = useGridRenderableRows(allRows);

    const scrollRef = useRef<HTMLDivElement>(null);
    const activeStickyIndexRef = useRef(0);

    const stickyIndexes = useMemo(() => [0], []);

    const rowVirtualizer = useVirtualizer({
        count: renderableRows.length + ROW_STICKY_COUNT,
        getScrollElement: () => scrollRef.current,
        overscan: 5,
        estimateSize: useCallback((index: number) => {
            if (!index) {
                return TOP_HEADERS_HEIGHT;
            }
            return ROW_HEIGHT;
        }, []),
        rangeExtractor: useCallback(
            range => {
                activeStickyIndexRef.current = [...stickyIndexes].reverse().find(index => range.startIndex >= index);

                const next = new Set([activeStickyIndexRef.current, ...defaultRangeExtractor(range)]);

                return [...next].sort((a, b) => a - b);
            },
            [stickyIndexes],
        ),
    });

    const rows = rowVirtualizer.getVirtualItems();
    const totalHeight = rowVirtualizer.getTotalSize();

    const render = useMemo(() => {
        if (!renderableRows.length) {
            return (
                <>
                    <Headers start={0} />
                    <div>Nothing to approve</div>
                </>
            );
        }
        return rows.map(({ index, start, key }) => {
            if (!index) {
                return <Headers key={String(key)} start={start} />;
            }
            const rowIndex = index - ROW_STICKY_COUNT;
            const row = renderableRows[rowIndex];
            return <RowWrapper key={String(key)} row={row} start={start} />;
        });
    }, [renderableRows, rows]);

    const innerScrollContainerStyles = useMemo(
        (): React.CSSProperties => ({
            position: 'relative',
            width: '100%',
            height: `${totalHeight}px`,
        }),
        [totalHeight],
    );

    return (
        <StrictMode>
            <ScrollToContextProvider value={scrollTo}>
                <FilterBar />
                <SelectionBar />
                <div ref={scrollRef} style={scrollContainerStyle}>
                    <div style={innerScrollContainerStyles}>{render}</div>
                </div>
            </ScrollToContextProvider>
        </StrictMode>
    );
});

const scrollContainerStyle: React.CSSProperties = {
    height: 'calc(100% - 50px - 60px - 40px)',
    width: `100%`,
    overflow: 'auto',
    maxWidth: '100%',
};

const FilterBar = () => {
    const {
        selectedProjectIds,
        setSelectedProjectIds,
        selectedUserIds,
        setSelectedUserIds,
        selectedActivityIds,
        setSelectedActivityIds,
        selectedApprovalStates,
        setSelectedApprovalStates,
        projects,
        users,
        activities,
        approvals,
    } = useApprovalStore(
        store => ({
            selectedUserIds: store.selectedUserIds,
            setSelectedUserIds: store.setSelectedUserIds,
            selectedProjectIds: store.selectedProjectIds,
            setSelectedProjectIds: store.setSelectedProjectIds,
            selectedActivityIds: store.selectedActivityIds,
            setSelectedActivityIds: store.setSelectedActivityIds,
            selectedApprovalStates: store.selectedApprovalStates,
            setSelectedApprovalStates: store.setSelectedApprovalStates,
            projects: store.projects,
            users: store.users,
            activities: store.activities,
            approvals: store.approvals,
        }),
        shallow,
    );

    const { config } = useTimeUIConfig();

    const projectFilterOptions = useMemo(
        (): IDropdownOption<ProjectDto>[] =>
            Object.values(projects).map(project => {
                return {
                    key: project.id,
                    text: project.name,
                    data: project,
                };
            }),
        [projects],
    );

    const userFilterOptions = useMemo((): IDropdownOption<TimeUserDto>[] => {
        const approvalArray = Object.values(approvals);
        return Object.values(users)
            .filter(user => approvalArray.some(approval => approval.userId === user.id))
            .map(user => {
                return {
                    key: user.id,
                    text: user.name,
                    data: user,
                };
            });
    }, [approvals, users]);

    const stateFilterOptions = useMemo(
        (): IDropdownOption<ApprovalState>[] =>
            APPROVAL_STATES.map(state => {
                return {
                    key: state,
                    text: state,
                    data: state,
                };
            }),
        [],
    );

    const activityFilterOptions = useMemo(
        (): IDropdownOption<ActivityDto>[] => [
            {
                key: CUSTOM_RATES,
                text: 'No activity',
            },
            ...Object.values(activities).map(activity => {
                return {
                    key: activity.id,
                    text: activity.name,
                    data: activity,
                };
            }),
        ],
        [activities],
    );

    return (
        <Stack horizontal horizontalAlign="end" styles={{ root: { height: 40 } }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                <AllOptionsDropdown options={projectFilterOptions} setSelectedKeys={setSelectedProjectIds} selectedKeys={selectedProjectIds} />
                <AllOptionsDropdown options={userFilterOptions} setSelectedKeys={setSelectedUserIds} selectedKeys={selectedUserIds} />
                {config?.approval?.projectManagerApproval?.useRates && (
                    <AllOptionsDropdown options={activityFilterOptions} setSelectedKeys={setSelectedActivityIds} selectedKeys={selectedActivityIds} />
                )}
                <AllOptionsDropdown
                    options={stateFilterOptions}
                    setSelectedKeys={setSelectedApprovalStates}
                    selectedKeys={selectedApprovalStates}
                    mode="default"
                />
            </Stack>
        </Stack>
    );
};

const TOGGLE_ALL_KEY = 'TOGGLE_ALL_KEY';
const AllOptionsDropdown = ({
    options,
    selectedKeys,
    setSelectedKeys,
    mode = 'filter',
}: {
    options: IDropdownOption<any>[];
    selectedKeys: string[];
    setSelectedKeys: React.Dispatch<React.SetStateAction<string[]>>;
    mode?: 'default' | 'filter';
}) => {
    const allSelected = useMemo(() => options.length && options.every(option => selectedKeys.includes(option.key as string)), [options, selectedKeys]);

    const keys = useMemo(() => {
        const keys = [...selectedKeys];
        if (allSelected) {
            keys.unshift(TOGGLE_ALL_KEY);
        }
        return keys;
    }, [allSelected, selectedKeys]);

    const opts = useMemo((): IDropdownOption<any>[] => {
        return [
            {
                key: TOGGLE_ALL_KEY,
                text: allSelected ? 'Deselect all' : 'Select all',
            },
            ...options,
        ];
    }, [allSelected, options]);

    const onChange = useCallback(
        (e: any, opt?: IDropdownOption<any>) => {
            if (opt) {
                if (opt.selected) {
                    if (opt.key === TOGGLE_ALL_KEY) {
                        setSelectedKeys(options.map(opt => opt.key as string));
                    } else {
                        setSelectedKeys(states => [...states, opt.key as string]);
                    }
                } else {
                    if (opt.key === TOGGLE_ALL_KEY) {
                        setSelectedKeys([]);
                    } else {
                        setSelectedKeys(states => states.filter(state => state !== opt.key));
                    }
                }
            }
        },
        [options, setSelectedKeys],
    );

    const DropdownComponent = useMemo(() => {
        if (mode === 'filter') {
            return FilterDropdown;
        }
        return Dropdown;
    }, [mode]);

    return (
        <DropdownComponent
            options={opts}
            onChange={onChange}
            selectedKeys={keys}
            multiSelect
            styles={{ root: { width: 180 } }}
            onRenderTitle={onRenderTitleWithDeselect}
        />
    );
};

const onRenderTitleWithDeselect: IRenderFunction<IDropdownOption<any>[]> = (props, defaultRender) => {
    return defaultRender(props.filter(opt => opt.key !== TOGGLE_ALL_KEY));
};
